import {
  addDays,
  addMonths,
  addYears,
  differenceInDays,
  format,
  getDaysInMonth,
  isAfter,
  parseISO,
  startOfDay,
  subDays,
} from 'date-fns'
import type {
  DefaultProcessData,
  ResidentApplicationData,
  ResidentDefaultFormValues,
  ResidentDetailResponse,
} from '../types'

export function parseYmd(value: unknown): Date | null {
  if (value === undefined || value === null || value === '') return null
  const s = String(value).trim()
  if (!s) return null
  const iso = /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : s
  try {
    const d = parseISO(iso.includes('T') ? iso : `${iso}T12:00:00`)
    return Number.isNaN(d.getTime()) ? null : d
  } catch {
    return null
  }
}

function parseMoney(value: unknown): number {
  if (value === undefined || value === null || value === '') return NaN
  const n = Number(String(value).replace(/[^0-9.-]/g, ''))
  return Number.isFinite(n) ? n : NaN
}

function toYmdDate(d: Date): string {
  return format(d, 'yyyy-MM-dd')
}

function absFloor(n: number): number {
  return n < 0 ? Math.ceil(n) || 0 : Math.floor(n)
}

/**
 * moment `later.diff(earlier, 'months')` as a float — replicates moment's `monthDiff` exactly so
 * calc parity matches Angular at end-of-month boundaries (date-fns `differenceInMonths` disagrees
 * there, e.g. 2026-01-29 → 2026-02-28 is 0 months in moment but 1 in date-fns). Uses date-fns
 * `addMonths` for the anchors (clamps to end-of-month, same as moment `.add`).
 */
function momentMonthDiffFloat(later: Date, earlier: Date): number {
  const whole =
    (earlier.getFullYear() - later.getFullYear()) * 12 + (earlier.getMonth() - later.getMonth())
  const anchor = addMonths(later, whole)
  const el = earlier.getTime()
  let adjust: number
  if (el - anchor.getTime() < 0) {
    const anchor2 = addMonths(later, whole - 1)
    adjust = (el - anchor.getTime()) / (anchor.getTime() - anchor2.getTime())
  } else {
    const anchor2 = addMonths(later, whole + 1)
    adjust = (el - anchor.getTime()) / (anchor2.getTime() - anchor.getTime())
  }
  return -(whole + adjust) || 0
}

/** moment `end.diff(start, 'months')` (integer, floored). */
export function momentDiffMonths(start: Date, end: Date): number {
  return absFloor(momentMonthDiffFloat(end, start))
}

/** moment `end.diff(start, 'years')` (integer, floored). */
function momentDiffYears(start: Date, end: Date): number {
  return absFloor(momentMonthDiffFloat(end, start) / 12)
}

/**
 * Mirrors Angular `dateDiff(startdate, enddate)` in `resident-edit-new` — moment-based whole
 * years/months/days decomposition (returns the within-year month component + day remainder).
 */
export function dateDiffCalendar(
  start: Date,
  end: Date,
): { months: number; days: number } | undefined {
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return undefined

  const years = momentDiffYears(start, end)
  const months = momentDiffMonths(start, end) - years * 12
  const advanced = addMonths(addYears(start, years), months)
  const days = differenceInDays(end, advanced)

  return {
    months: Math.max(0, months),
    days: Math.max(0, days),
  }
}

function capReimbursementPeriod(months: number, days: number): { months: number; days: number } {
  let endMonths = months
  let endDays = days
  if (endMonths >= 6) {
    endMonths = 6
    endDays = 0
  }
  if (endMonths < 0) endMonths = 0
  if (endDays < 0) endDays = 0
  return { months: endMonths, days: endDays }
}

/**
 * Angular's `months_lost` / `lost_revenue` (`lossnewCalculation`) applies ONLY a negative clamp —
 * it is NOT capped at 6 months like the reimbursement period. See `resident-edit-new` calc block.
 */
function clampNonNegativePeriod(months: number, days: number): { months: number; days: number } {
  return { months: Math.max(0, months), days: Math.max(0, days) }
}

function formatPeriod(months: number, days: number): string {
  return `${months}m ${days}d`
}

/** Divisor shown as “(N Days)” next to Daily Reimbursement Rate for liberty roles. */
export function getDailyRentMonthDaysCount(
  detail: ResidentDetailResponse | null | undefined,
  periodEndYmd: string | undefined | null,
): number {
  if (!isDailyRentCalculationOld(detail)) return 30
  const periodEnd = parseYmd(periodEndYmd)
  return periodEnd ? getDaysInMonth(periodEnd) : 30
}

/** Angular sets `isDailyRentCalculationOld` from payment review log approval dates. */
export function isDailyRentCalculationOld(detail: ResidentDetailResponse | null | undefined): boolean {
  // Angular iterates `residentDetail.payment_history` (NOT payment_review_log) for the legacy flag.
  const log = detail?.payment_history
  if (!log?.length) return false

  const legacyCutoff = parseISO('2021-02-01T12:00:00').getTime()

  const toMs = (value: unknown): number | null => {
    if (value === undefined || value === null || value === '') return null
    if (typeof value === 'number') {
      return value < 1_000_000_000_000 ? value * 1000 : value
    }
    const ms = Date.parse(String(value))
    return Number.isNaN(ms) ? null : ms
  }

  return log.some((row) => {
    const created = toMs(row?.created_at)
    const insurance = toMs(row?.insurance_review_approval_date)
    const liberty = toMs(row?.liberty_authorization_approval_date)
    return (
      (created !== null && created < legacyCutoff) ||
      (insurance !== null && insurance < legacyCutoff) ||
      (liberty !== null && liberty < legacyCutoff)
    )
  })
}

/**
 * Mirrors Angular `calculationBaseonDate()` max end date + `max_reason` when `unit_key` is present.
 */
export function computeMaxMoveInDateAndReason(
  formValues: ResidentDefaultFormValues,
  applicationData: ResidentApplicationData | null | undefined,
  detail: ResidentDetailResponse | null | undefined,
): Pick<DefaultProcessData, 'max_movein_date' | 'max_reason'> {
  if (!detail?.unit_key) return {}

  const calculationDate = formValues.adjusted_notice || formValues.rental_status_date
  const adjNotice = parseYmd(calculationDate)
  if (!adjNotice) return {}

  let maxReason = 'Reimbursement Period Limit'
  let maxMoveDate = subDays(addMonths(adjNotice, 6), 1)

  if (formValues.first_month_not_paid_month && formValues.first_month_not_paid_year) {
    const firstUnpaid = parseYmd(
      `${formValues.first_month_not_paid_year}-${String(formValues.first_month_not_paid_month).padStart(2, '0')}-01`,
    )
    if (firstUnpaid) {
      maxMoveDate = subDays(addMonths(firstUnpaid, 6), 1)
      maxReason = 'Reimbursements Period Limit'
    }
  }

  const contractEnd = parseYmd(applicationData?.contract_end_date)
  if (contractEnd && isAfter(startOfDay(maxMoveDate), startOfDay(contractEnd))) {
    maxMoveDate = contractEnd
    maxReason = 'Contract End'
  }

  let finalMoveDate = maxMoveDate
  const newResidentLease = parseYmd(formValues.new_resident_lease_start_date)
  if (newResidentLease) {
    if (isAfter(startOfDay(newResidentLease), startOfDay(finalMoveDate))) {
      finalMoveDate = maxMoveDate
    } else {
      finalMoveDate = subDays(newResidentLease, 1)
      maxReason = 'New Resident'
    }
  }

  return {
    max_movein_date: toYmdDate(finalMoveDate),
    max_reason: maxReason,
  }
}

function resolveFirstMonthNotPaidStart(formValues: ResidentDefaultFormValues): Date | null {
  if (formValues.first_month_not_paid_month && formValues.first_month_not_paid_year) {
    return parseYmd(
      `${formValues.first_month_not_paid_year}-${String(formValues.first_month_not_paid_month).padStart(2, '0')}-01`,
    )
  }
  return parseYmd(formValues.rental_status_date)
}

/**
 * Full client-side reimbursement calculation — mirrors Angular `calculationBaseonDate()` +
 * `adjustment()` when `unit_key` is present.
 */
export function computeCalculationFromFormValues(
  formValues: ResidentDefaultFormValues,
  applicationData: ResidentApplicationData | null | undefined,
  detail: ResidentDetailResponse | null | undefined,
): Partial<DefaultProcessData> {
  if (!detail?.unit_key) return {}

  const rent = parseMoney(applicationData?.rent_amount)
  if (!Number.isFinite(rent) || rent <= 0) return {}

  const adjNotice = parseYmd(formValues.adjusted_notice || formValues.rental_status_date)
  const rentalStatusDate = parseYmd(formValues.rental_status_date)
  if (!adjNotice || !rentalStatusDate) return {}

  const maxFields = computeMaxMoveInDateAndReason(formValues, applicationData, detail)

  // Period uses the same End Date the picker shows (form). Callers hydrate/patch form to
  // system max when not Manual Override (`buildDetailFormValues` + live End Date patch).
  // Form-first also covers post-override-save before detail refresh (picker already updated).
  const periodEnd =
    parseYmd(formValues.adjust_max_movein_date) || parseYmd(maxFields.max_movein_date)
  if (!periodEnd) return { ...maxFields }

  /** Angular: `fialMoveinDate = finalDate + 1 day` before `dateDiff(adj_notice, fialMoveinDate)`. */
  const periodEndExclusive = addDays(periodEnd, 1)
  const periodCalc = dateDiffCalendar(adjNotice, periodEndExclusive)
  const capped = capReimbursementPeriod(periodCalc?.months ?? 0, periodCalc?.days ?? 0)

  const useLegacyDailyDivisor = isDailyRentCalculationOld(detail)
  const monthDaysCount = useLegacyDailyDivisor ? getDaysInMonth(periodEnd) : 30
  const dailyRent = rent / monthDaysCount
  // Angular `gross_daily_rent` uses days-in-month of the exclusive end when legacy.
  const grossMonthDaysCount = useLegacyDailyDivisor ? getDaysInMonth(periodEndExclusive) : 30
  const grossDailyRent = rent / grossMonthDaysCount

  const lossRent = parseFloat((capped.months * rent + capped.days * dailyRent).toFixed(2))

  // Angular: `moment(rental_status_date).diff(moment(contract_start_date), 'months')` — day-aware
  // whole-month truncation, negatives allowed (NOT calendar-month boundaries, NOT clamped to >= 0).
  const contractStart = parseYmd(applicationData?.contract_start_date || applicationData?.lease_start_date)
  const leaseMonth =
    contractStart && rentalStatusDate
      ? momentDiffMonths(contractStart, rentalStatusDate)
      : undefined

  let monthsLost = '0m 0d'
  let lostRevenue = 0
  let grossDefaultAmount: number | undefined
  const adjustedNotice = parseYmd(formValues.adjusted_notice)
  if (adjustedNotice) {
    const firstMonth = resolveFirstMonthNotPaidStart(formValues)
    if (firstMonth) {
      const lostCalc = dateDiffCalendar(firstMonth, adjustedNotice)
      if (lostCalc) {
        // months_lost / lost_revenue are NOT capped at 6 months in Angular — only negative-clamped.
        const lostClamped = clampNonNegativePeriod(lostCalc.months, lostCalc.days)
        monthsLost = formatPeriod(lostClamped.months, lostClamped.days)
        lostRevenue = parseFloat((lostClamped.months * rent + lostClamped.days * dailyRent).toFixed(2))
      }

      // Angular `gross_default_amount`: dateDiff(firstMonth, exclusiveEnd), capped at 6m, gross daily rent.
      const grossCalc = dateDiffCalendar(firstMonth, periodEndExclusive)
      if (grossCalc) {
        const grossCapped = capReimbursementPeriod(grossCalc.months, grossCalc.days)
        grossDefaultAmount = parseFloat(
          (grossCapped.months * rent + grossCapped.days * grossDailyRent).toFixed(2),
        )
      }
    }
  }

  const base: Partial<DefaultProcessData> = {
    ...maxFields,
    lease_month: leaseMonth,
    notice_month_diff: formatPeriod(capped.months, capped.days),
    daily_rent: dailyRent,
    gross_daily_rent: grossDailyRent,
    calculation_month_days: monthDaysCount,
    loss_rent: lossRent,
    months_lost: monthsLost,
    lost_revenue: lostRevenue,
    rent_amount: applicationData?.rent_amount,
    ...(grossDefaultAmount !== undefined ? { gross_default_amount: grossDefaultAmount } : {}),
  }

  const lossAdjustment = parseMoney(formValues.loss_adjustment)
  const unusedSecurity = parseMoney(formValues.unused_security_deposit)
  const partialPayment = parseMoney(formValues.partial_payment_amount)
  let editedAmount = 0
  if (Number.isFinite(lossAdjustment)) editedAmount += lossAdjustment
  if (Number.isFinite(unusedSecurity)) editedAmount += unusedSecurity
  if (Number.isFinite(partialPayment)) editedAmount += partialPayment

  // Angular `adjustment()` writes net/deduction figures ONLY when editedAmount >= 0; a net-negative
  // total leaves the prior net values untouched.
  if (editedAmount < 0) return base

  let netDue = parseFloat((lossRent - editedAmount).toFixed(2))
  // Angular `setResidentData`: for each DEFAULT_INVOICE in payment_history, net_due -= payment_amount.
  for (const row of detail.payment_history || []) {
    if (row?.payment_type === 'DEFAULT_INVOICE') {
      netDue -= Number(row.payment_amount) || 0
    }
  }
  netDue = parseFloat(netDue.toFixed(2))
  const claimStatus = String(detail.resident_default?.claim_status || formValues.claim_status || '')

  return {
    ...base,
    // Deductions are displayed as whole-dollar rounded values (net math uses the unrounded sum).
    loss_adjustment: Math.round(Number.isFinite(lossAdjustment) ? lossAdjustment : 0),
    partial_payment_amount: Math.round(Number.isFinite(partialPayment) ? partialPayment : 0),
    unused_security_deposit: Math.round(Number.isFinite(unusedSecurity) ? unusedSecurity : 0),
    net_due: claimStatus === 'DENIED' ? 0 : netDue,
    net_amount: netDue,
  }
}

/**
 * When GET `/api/default` omits calculation fields, Angular still fills the tab via
 * `calculationBaseonDate()`. With `unit_key`, recompute live from form values (same as Angular).
 */
export function enrichCalculationSnapshotWithClientFallback(
  snapshot: DefaultProcessData,
  detail: ResidentDetailResponse | null | undefined,
  applicationData: ResidentApplicationData | null | undefined,
  formValues: ResidentDefaultFormValues,
): DefaultProcessData {
  const out: DefaultProcessData = { ...snapshot }

  if (detail?.unit_key) {
    const computed = computeCalculationFromFormValues(formValues, applicationData, detail)
    return { ...out, ...computed }
  }

  // Angular's `calculationBaseonDate()` sets ONLY `lease_month` when `unit_key` is absent — it does
  // NOT compute daily_rent / loss_rent / net_due / months_lost / lost_revenue / max fields for
  // non-unit residents. Those come from the server snapshot as-is.
  const contractStart = parseYmd(applicationData?.contract_start_date || applicationData?.lease_start_date)
  const rentalStatusDate = parseYmd(formValues.rental_status_date)
  if (contractStart && rentalStatusDate) {
    out.lease_month = momentDiffMonths(contractStart, rentalStatusDate)
  }

  return out
}
